import {
  ObservationToolObservationStatus,
  ObservationToolObservationOpenStatusStandings,
  ObservationToolObservationCompleteStatusStandings,
  ObservationToolObservationStandingDisabled,
  ObservationToolObservationInformationalStatusStandings,
} from 'src/app/shared/models/autogenerated';

export class ObservationToolDashboardRoutes {
  static readonly DELIVERABLES = 'deliverables';
  static readonly OBSERVATIONS = 'observations';
  static readonly CHECKS = 'checks';
}

export type ObservationToolDashboardPageView =
  | typeof ObservationToolDashboardRoutes.DELIVERABLES
  | typeof ObservationToolDashboardRoutes.OBSERVATIONS
  | typeof ObservationToolDashboardRoutes.CHECKS;

export const EQUIPMENT_NUMBER_MAX_LENGTH = 50;
export const TITLE_MAX_LENGTH = 100;
export const OBSERVATION_MAX_LENGTH = 10000;
export const CONCERN_MAX_LENGTH = 10000;
export const RECOMMENDATION_MAX_LENGTH = 10000;
export const ACTION_ITEM_MAX_LENGTH = 10000;
export const DA_COMMENTS_MAX_LENGTH = 10000;
export const POTENTIAL_IMPACT_DESCRIPTION_MAX_LENGTH = 1000;
export const CES_DESCRIPTION_MAX_LENGTH = 100;
export const OTT_OBSERVATION_DASHBOARD_MAX_PER_PANEL = 8;
export const OTT_CHECK_DASHBOARD_MAX_PER_PANEL = 8;
export const DELIVERABLE_FLAG_MAX_LENGTH = 1000;
export const DELIVERABLE_NOTE_MAX_LENGTH = 10000;
export const RESOLVE_DELIVERABLE_FLAG_MAX_LENGTH = 1000;
export const OTT_CHECK_NOTES_CHARACTER_COUNTER_MAX_LENGTH = 10000;
export const SCOPE_OF_REVIEW_MAX_LENGTH = 80;
export const DISCUSSION_MESSAGE_INPUT_MAX_LENGTH = 30000;

export const DETAILS_OBSERVATION_FULLSCREEN_ID = 'obs_details_observations';
export const DETAILS_ACTIVITY_FULLSCREEN_ID = 'obs_details_activity';
export const OBSERVATION_FORM_FULLSCREEN_ID = 'obs_observation_form';
export const OBSERVATION_TOOL_DOCUMENTS_FULLSCREEN_ID = 'obs_documents';
export const OBSERVATION_FORM_DISCUSSION_FULLSCREEN_ID =
  'obs_observation_form_discussion';
export const OBSERVATION_TOOL_CHECK_CHAT_DISCUSSION_FULLSCREEN_ID =
  'obs_observation_tool_check_chat_discussion';
export const OBSERVATION_TOOL_EDIT_NOTES_FULLSCREEN_ID =
  'obs_deliverable_notes';
  export const OBSERVATION_TOOL_NOTES_FULLSCREEN_ID =
  'obs_deliverable_notes';
export const RISK_ASSESSMENT_NOTES_LIST_FULLSCREEN_ID =
  'risk_assessment_notes';
  
export const SORT_BY_BOOKMARKED = 'bookmarked';
export const SORT_BY_ALPHABETICAL_ASC = 'alphabeticalAsc';
export const SORT_BY_ALPHABETICAL_DESC = 'alphabeticalDesc';

export const AreaSelections = ['plant A', 'plant B', 'plant C'];

export const NotApplicable = 'n/a';

export enum VandVStatus {
  vandVComplete = 'v&v complete',
  vandVReady = 'v&v ready',
  vandVNotReady = 'v&v not ready',
}

export const timeFrameToAddressOptions = [
  'now, stop work until fully addressed',
  'phase 2',
  'phase 3',
  'before FID',
  'phase 4',
  'before start-up',
  'phase 5',
];

export const CESImpactOptions = [
  {
    viewValue: 'yes',
    value: true,
  },
  { viewValue: 'no', value: false },
];

export const ActionStatus = [
  'assigned',
  'mitigation plan agreed',
  'mitigation validated-pending doc',
  'complete',
];

export const TechnicalDiscipline = [
  'civil / structural',
  'electrical engineering',
  'eng management',
  'fire protection & fire and gas',
  'flare and relief',
  'flow assurance',
  'heat exchanger design',
  'machinery',
  'materials engineering',
  'operations',
  'other',
  'piping and valves',
  'piping stress',
  'process automation',
  'process safety',
  'project engineering',
  'quality management',
  'reliability and integrity',
  'risers',
  'subsea',
  'TC&S',
  'TQP',
];

export const ObservationId = 'observationId';

export const StandingsByStatus: { [status: string]: string[] } = {
  [ObservationToolObservationStatus.Draft.toString()]: [
    ObservationToolObservationStandingDisabled.None,
  ],
  [ObservationToolObservationStatus.Working.toString()]: [
    ObservationToolObservationStandingDisabled.None,
  ],
  [ObservationToolObservationStatus.Open.toString()]: Object.values(
    ObservationToolObservationOpenStatusStandings
  ),
  [ObservationToolObservationStatus.Complete.toString()]: Object.values(
    ObservationToolObservationCompleteStatusStandings
  ),
  [ObservationToolObservationStatus.Informational.toString()]: Object.values(
    ObservationToolObservationInformationalStatusStandings
  ),
};

export const OBSERVATION_FORM_OBSERVATIONS_OUTLET = 'observations';
export const OBSERVATION_FORM_INITIAL_OBSERVATION = 'initial';
export const OBSERVATION_FORM_FINAL_OBSERVATION = 'final';

export const OBSERVATION_FORM_INITIAL_OBSERVATIONS_OUTLET_LINK = [
  '/',
  {
    outlets: {
      [OBSERVATION_FORM_OBSERVATIONS_OUTLET]: [
        OBSERVATION_FORM_INITIAL_OBSERVATION,
      ],
    },
  },
];

export const OBSERVATION_FORM_FINAL_OBSERVATIONS_OUTLET_LINK = [
  '/',
  {
    outlets: {
      [OBSERVATION_FORM_OBSERVATIONS_OUTLET]: [
        OBSERVATION_FORM_FINAL_OBSERVATION,
      ],
    },
  },
];

export enum NextActionWith {
  You = 'you',
}

export enum ObservationToolNoneChannel {
  None = 'None',
}

export const ObservationToolStatusToolTips: Map<string, string> = new Map<
  string,
  string
>([
  [
    ObservationToolObservationStatus.Draft,
    'in draft, only the SME can see the observation until the SME chooses to click add observation',
  ],
  [
    ObservationToolObservationStatus.Working,
    'in working, the DA engineer and SME can see the observation. DA engineer will review and choose the next status/standing',
  ],
  [
    ObservationToolObservationStatus.Open,
    'in open, all parties are working toward agreement on a path forward',
  ],
  [
    ObservationToolObservationStatus.Complete,
    'complete observation indicates no further action on the observation is required',
  ],
  [
    ObservationToolObservationStatus.Informational,
    'informational with pending follow-Up standing will be revisited in subsequent phase reviews',
  ],
]);

export const ObservationToolStandingToolTips: Map<string, string> = new Map<
  string,
  string
>([
  [
    ObservationToolObservationOpenStatusStandings.InDiscussion,
    'action items to resolve the observation must be decided on; please use the chat to clarify a path forward',
  ],
  [
    ObservationToolObservationOpenStatusStandings.MitigationPlanAgreedPendingDocuments,
    'mitigation plan has been agreed upon by all parties, awaiting project action to resolve the observation',
  ],
  [
    ObservationToolObservationOpenStatusStandings.PendingMitigationPlan,
    'awaiting action to resolve the observation',
  ],
  [
    ObservationToolObservationOpenStatusStandings.NoMitigationPlanAgreedEngageTechnicalAuthority,
    'awaiting decision from technical authority on path forward',
  ],
  [
    ObservationToolObservationOpenStatusStandings.NoMitigationPlanAgreedUnresolved,
    'no mitigation plan agreed; please reach out to DA to reopen discussion',
  ],
  [
    ObservationToolObservationOpenStatusStandings.RequestForClarification,
    'pending clarification from the project',
  ],
  [
    ObservationToolObservationCompleteStatusStandings.WithAction,
    'action has been completed by project',
  ],
  [
    ObservationToolObservationCompleteStatusStandings.WithoutAction,
    'action was not completed by project',
  ],
  [
    ObservationToolObservationInformationalStatusStandings.MitigationPlanNotRequired,
    'no mitigation plan required - project team can take action they see fit to resolve this observation',
  ],
]);

export enum ObservationToolVandVToolTip {
  VandVReady = 'V&V ready',
  VandVNotReady = 'V&V not ready',
}

export const ObservationToolAddDocumentBtnInfoToolTip =
  'adding document revision is only available when the status is "open" with the standing of "mitigation plan agreed, pending documents"';

export class ObservationToolChecksRoutes {
  static readonly DELIVERABLES = 'deliverables';
  static readonly OBSERVATIONS = 'observations';
  static readonly CHECKS = 'checks';
}

export enum filterGroupCarryOverOptions {
  isCarryOver = 'is carry-over',
  isNotCarryOver = 'is not carry-over',
}

export const ClarificationAndStrengthToolTip =
  'you cannot rank a clarification or a strength observation. to rank this observation, please change the DA theme.';

export const StatusAndStandingReferenceInfo =
  'open status and standing reference';
export const StatusAndStandingReferenceUrl =
  'assets/documents/status-and-standing-reference.pdf';
